import React, { useEffect, useState, useRef } from 'react';
import { useTour } from '../contexts/TourContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export const TourOverlay: React.FC = () => {
    const { isTourActive, currentStep, tourSteps, nextStep, previousStep, stopTour } = useTour();
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isTourActive) return;

        let animationFrameId: number;

        const updatePosition = () => {
            const step = tourSteps[currentStep];
            if (!step) return;

            // Handle CENTER placement
            if (step.placement === 'center') {
                setTargetRect({ width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0, x: 0, y: 0, toJSON: () => { } });

                const tooltipWidth = tooltipRef.current?.offsetWidth || 400;
                const tooltipHeight = tooltipRef.current?.offsetHeight || 300;

                setTooltipPosition({
                    top: window.innerHeight / 2 - tooltipHeight / 2,
                    left: window.innerWidth / 2 - tooltipWidth / 2
                });
            } else {
                let element: Element | null = null;
                const target = step.target;

                try {
                    // Pre-check for custom pseudo-selectors to avoid SyntaxError in querySelector
                    const isCustomSelector = target.includes(':contains') || target.includes(':has');

                    if (!isCustomSelector) {
                        element = document.querySelector(target);
                    }

                    // Handle custom pseudoselectors
                    if (!element && target.includes(':contains')) {
                        const match = target.match(/(.*?):contains\("(.*?)"\)/);
                        if (match) {
                            const tag = match[1] || '*';
                            const text = match[2];
                            const elements = Array.from(document.querySelectorAll(tag));
                            element = elements.find(el => el.textContent?.includes(text)) || null;
                        }
                    }

                    if (!element && target.includes(':has')) {
                        // Very basic :has fallback for simple nested classes
                        const match = target.match(/(.*?):has\(\.(.*?)\)/);
                        if (match) {
                            const parentTag = match[1] || '*';
                            const childClass = match[2];
                            const elements = Array.from(document.querySelectorAll(parentTag));
                            element = elements.find(el => el.querySelector('.' + childClass)) || null;
                        }
                    }

                    // Final fallback to native querySelector if it's not a custom one we handled
                    if (!element && !isCustomSelector) {
                        element = document.querySelector(target);
                    }
                } catch (e) {
                    console.warn(`Invalid selector in tour step: ${target}`, e);
                }

                if (element) {
                    const rect = element.getBoundingClientRect();
                    setTargetRect(rect);

                    const tooltipWidth = tooltipRef.current?.offsetWidth || 350;
                    const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
                    let top = 0, left = 0;

                    switch (step.placement) {
                        case 'right':
                            top = rect.top + rect.height / 2 - tooltipHeight / 2;
                            left = rect.right + 20;
                            break;
                        case 'left':
                            top = rect.top + rect.height / 2 - tooltipHeight / 2;
                            left = rect.left - tooltipWidth - 20;
                            break;
                        case 'bottom':
                            top = rect.bottom + 20;
                            left = rect.left + rect.width / 2 - tooltipWidth / 2;
                            break;
                        case 'top':
                        default:
                            top = rect.top - tooltipHeight - 20;
                            left = rect.left + rect.width / 2 - tooltipWidth / 2;
                            break;
                    }

                    if (left < 10) left = 10;
                    if (left + tooltipWidth > window.innerWidth - 10) left = window.innerWidth - tooltipWidth - 10;
                    if (top < 10) top = 10;
                    if (top + tooltipHeight > window.innerHeight - 10) top = window.innerHeight - tooltipHeight - 10;

                    setTooltipPosition({ top, left });
                } else {
                    // Fallback to center if element not found or selector invalid
                    setTargetRect({ width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0, x: 0, y: 0, toJSON: () => { } } as DOMRect);
                    const tooltipWidth = tooltipRef.current?.offsetWidth || 400;
                    const tooltipHeight = tooltipRef.current?.offsetHeight || 300;
                    setTooltipPosition({
                        top: window.innerHeight / 2 - tooltipHeight / 2,
                        left: window.innerWidth / 2 - tooltipWidth / 2
                    });
                }
            }

            animationFrameId = requestAnimationFrame(updatePosition);
        };

        updatePosition();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [isTourActive, currentStep, tourSteps]);

    if (!isTourActive || !targetRect) return null;

    const step = tourSteps[currentStep];
    if (!step) return null;

    return (
        <>
            {/* Dark overlay */}
            <div
                className="fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300"
                onClick={stopTour}
            />

            {/* Yellow halo spotlight */}
            {/* Yellow halo spotlight */}
            {step.placement !== 'center' && targetRect.width > 0 && (
                <div
                    className="fixed z-[9999] pointer-events-none transition-all duration-300"
                    style={{
                        top: targetRect.top - 8,
                        left: targetRect.left - 8,
                        width: targetRect.width + 16,
                        height: targetRect.height + 16,
                        boxShadow: '0 0 0 4px rgba(250, 204, 21, 0.6), 0 0 20px 8px rgba(250, 204, 21, 0.4)',
                        borderRadius: '12px',
                        border: '3px solid #fbbf24',
                        background: 'transparent',
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="fixed z-[10000] bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-md transition-all duration-300"
                style={{
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                }}
            >
                {/* Header */}
                <div className="flex items-start justify-between p-4 border-b border-border">
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-text-primary">{step.title}</h3>
                        <p className="text-xs text-text-secondary mt-1">
                            Step {currentStep + 1} of {tourSteps.length}
                        </p>
                    </div>
                    <button
                        onClick={stopTour}
                        className="text-text-secondary hover:text-text-primary transition-colors"
                        title="Exit Tour"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <p className="text-text-primary leading-relaxed">{step.content}</p>
                </div>

                {/* Footer with navigation */}
                <div className="flex items-center justify-between p-4 border-t border-border bg-background">
                    <button
                        onClick={previousStep}
                        disabled={currentStep === 0}
                        className="flex items-center gap-2 px-3 py-2 rounded bg-card border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                        <ChevronLeft size={16} />
                        Previous
                    </button>

                    <div className="flex gap-1">
                        {tourSteps.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all ${index === currentStep
                                    ? 'bg-primary w-6'
                                    : index < currentStep
                                        ? 'bg-primary/50'
                                        : 'bg-border'
                                    }`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={nextStep}
                        className="flex items-center gap-2 px-3 py-2 rounded bg-primary text-white hover:bg-secondary transition-colors text-sm font-medium"
                    >
                        {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </>
    );
};
